import { useContext, useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../../auth/AuthContext";
import PharmacySelector from "../home/PharmacySelector";
import api from "../../api/client";
import { Toast } from "primereact/toast";

// ── Helpers (shared with NotificationsPage) ──────────────────────────────────
import { formatRelativeTime, NOTI_ICONS, resolveRoute } from "../util/notificationUtils";

// ── Logo ──────────────────────────────────────────────────────────────────────
function LogoSvg({ size = 28 }) {
  return (
    <svg viewBox="0 0 100 100" width={size} height={size}>
      <circle cx="50" cy="24" r="10" fill="white"/>
      <line x1="50" y1="34" x2="50" y2="68" stroke="white" strokeWidth="7" strokeLinecap="round"/>
      <polyline points="50,51 30,45 35,28" fill="none" stroke="white" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round"/>
      <polyline points="50,51 70,45 65,28" fill="none" stroke="white" strokeWidth="7" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M25,70 Q50,90 75,70" fill="none" stroke="white" strokeWidth="7" strokeLinecap="round"/>
    </svg>
  );
}

// ── Notification dropdown ─────────────────────────────────────────────────────

function NotificationDropdown({
  notifications, unreadCount, hasMore, loadingMore,
  onMarkRead, onMarkOneRead, onClose, onLoadMore, navigate,
}) {
  const listRef  = useRef(null);
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 600;

  // Detect scroll-to-bottom for lazy loading
  const handleScroll = useCallback(() => {
    const el = listRef.current;
    if (!el || loadingMore || !hasMore) return;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) {
      onLoadMore();
    }
  }, [loadingMore, hasMore, onLoadMore]);

  const panelStyle = isMobile ? {
    position: "fixed",
    top: 64, left: 12, right: 12, width: "auto",
    zIndex: 200,
    background: "var(--surface-card)",
    border: "1px solid var(--border-color)",
    borderRadius: 14,
    boxShadow: "0 8px 30px rgba(15,41,68,0.12)",
    overflow: "hidden",
  } : {
    position: "absolute",
    top: "calc(100% + 10px)", right: 0,
    width: 380, zIndex: 200,
    background: "var(--surface-card)",
    border: "1px solid var(--border-color)",
    borderRadius: 14,
    boxShadow: "0 8px 30px rgba(15,41,68,0.12)",
    overflow: "hidden",
  };

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 199 }} />
      <div style={panelStyle}>

        {/* Header */}
        <div style={{
          padding: "14px 18px 12px",
          borderBottom: "1px solid var(--border-light)",
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: "var(--text-primary)", display: "flex", alignItems: "center", gap: 8 }}>
            Notifications
            {unreadCount > 0 && (
              <span style={{
                fontSize: 11, fontWeight: 700,
                background: "#ef4444", color: "white",
                padding: "2px 8px", borderRadius: 20,
                minWidth: 20, textAlign: "center",
              }}>{unreadCount}</span>
            )}
          </div>
          {unreadCount > 0 && (
            <button onClick={onMarkRead} style={{
              fontSize: 12, color: "var(--brand-primary)", fontWeight: 500,
              background: "none", border: "none", cursor: "pointer",
              fontFamily: "inherit", padding: "2px 6px", borderRadius: 6,
            }}>Mark all read</button>
          )}
        </div>

        {/* List */}
        <div
          ref={listRef}
          onScroll={handleScroll}
          style={{ maxHeight: isMobile ? "calc(100dvh - 180px)" : 420, overflowY: "auto" }}
        >
          {notifications.length === 0 ? (
            <div style={{ padding: "36px 20px", textAlign: "center", color: "var(--text-muted)", fontSize: 13 }}>
              <i className="pi pi-bell" style={{ fontSize: 24, display: "block", marginBottom: 8 }} />
              No notifications yet
            </div>
          ) : (
            notifications.map((n, idx) => {
              const meta    = NOTI_ICONS[n.noti_type] ?? NOTI_ICONS.default;
              const route   = resolveRoute(n);
              const isLast  = idx === notifications.length - 1;
              return (
                <div
                  key={n.id}
                  onClick={() => {
                    if (!n.is_read) onMarkOneRead(n.id);
                    if (route) { navigate(route); onClose(); }
                  }}
                  style={{
                    display: "flex", gap: 12, alignItems: "flex-start",
                    padding: "12px 18px",
                    borderBottom: isLast ? "none" : "1px solid var(--border-light)",
                    background: n.is_read ? "transparent" : "var(--brand-light)",
                    cursor: route ? "pointer" : "default",
                    transition: "background 0.15s",
                  }}
                  onMouseEnter={e => { if (route) e.currentTarget.style.background = n.is_read ? "var(--surface-hover)" : "#dbeafe"; }}
                  onMouseLeave={e => { e.currentTarget.style.background = n.is_read ? "transparent" : "var(--brand-light)"; }}
                >
                  {/* Icon */}
                  <div style={{
                    width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                    background: meta.bg, color: meta.color,
                    display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15,
                  }}>
                    <i className={`pi ${meta.icon}`} />
                  </div>

                  {/* Text */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{
                      fontSize: 13, fontWeight: n.is_read ? 500 : 700,
                      color: "var(--text-primary)", lineHeight: 1.4,
                    }}>
                      {n.title}
                    </div>
                    {/* ✅ Fix 5: let message wrap fully, no truncation */}
                    <div style={{
                      fontSize: 12.5, color: "var(--text-secondary)",
                      marginTop: 3, lineHeight: 1.5,
                    }}>
                      {n.message}
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 4 }}>
                      {formatRelativeTime(n.created_at)}
                    </div>
                  </div>

                  {/* ✅ Fix 1: unread dot — bigger, vivid red, never hidden */}
                  {!n.is_read && (
                    <div style={{
                      width: 10, height: 10, borderRadius: "50%",
                      background: "#ef4444",
                      flexShrink: 0, marginTop: 4,
                      boxShadow: "0 0 0 2px white",
                    }} />
                  )}
                </div>
              );
            })
          )}

          {/* Lazy-load indicator */}
          {loadingMore && (
            <div style={{ padding: "12px", textAlign: "center" }}>
              <i className="pi pi-spin pi-spinner" style={{ fontSize: 16, color: "var(--brand-primary)" }} />
            </div>
          )}
          {!hasMore && notifications.length > 0 && (
            <div style={{ padding: "10px", textAlign: "center", fontSize: 11.5, color: "var(--text-muted)" }}>
              All caught up
            </div>
          )}
        </div>


      </div>
    </>
  );
}

// ── Avatar dropdown ───────────────────────────────────────────────────────────
function AvatarDropdown({ user, onClose, navigate, onLogout }) {
  const fullName = [user?.first_name, user?.last_name].filter(Boolean).join(" ") || "My Account";
  const phone    = user?.phone ?? "";

  const menuItems = [
    { icon: "pi-user",     label: "View profile", action: () => { navigate("/profile"); onClose(); } },
    { icon: "pi-sign-out", label: "Log out",      action: onLogout, danger: true },
  ];

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, zIndex: 199 }} />
      <div style={{
        position: "absolute", top: "calc(100% + 10px)", right: 0,
        width: 220, zIndex: 200,
        maxWidth: "calc(100vw - 24px)",
        background: "var(--surface-card)",
        border: "1px solid var(--border-color)",
        borderRadius: 14,
        boxShadow: "0 8px 30px rgba(15,41,68,0.12)",
        overflow: "hidden",
      }}>
        <div style={{ padding: "14px 16px 12px", borderBottom: "1px solid var(--border-light)" }}>
          <div style={{ fontWeight: 600, fontSize: 13.5, color: "var(--text-primary)" }}>{fullName}</div>
          <div style={{ fontSize: 12, color: "var(--text-muted)", marginTop: 2 }}>{phone}</div>
        </div>
        <div style={{ padding: "6px 0" }}>
          {menuItems.map((item) => (
            <button
              key={item.label}
              onClick={item.action}
              style={{
                width: "100%", display: "flex", alignItems: "center", gap: 10,
                padding: "9px 16px", border: "none", background: "none",
                cursor: "pointer", fontFamily: "inherit", fontSize: 13,
                color: item.danger ? "var(--status-danger)" : "var(--text-primary)",
                textAlign: "left", transition: "background 0.15s",
              }}
              onMouseEnter={e => e.currentTarget.style.background = item.danger ? "#fef2f2" : "var(--surface-hover)"}
              onMouseLeave={e => e.currentTarget.style.background = "none"}
            >
              <i className={`pi ${item.icon}`} style={{
                fontSize: 13,
                color: item.danger ? "var(--status-danger)" : "var(--text-muted)",
              }} />
              {item.label}
            </button>
          ))}
        </div>
      </div>
    </>
  );
}

// ── Main header ───────────────────────────────────────────────────────────────
const PAGE_SIZE = 15;

export default function PortalHeader({ onMenuClick }) {
  const { user, orgId, logout: authLogout } = useContext(AuthContext);
  const navigate = useNavigate();

  const [notifications, setNotifications] = useState([]);
  const [unreadCount,   setUnreadCount]   = useState(0);
  const [bellOpen,      setBellOpen]      = useState(false);
  const [avatarOpen,    setAvatarOpen]    = useState(false);

  // Pagination state
  const [page,        setPage]        = useState(1);
  const [hasMore,     setHasMore]     = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const toast        = useRef(null);
  const wsRef        = useRef(null);
  const pendingWsIds = useRef(new Set());

  const initials = [user?.first_name?.[0], user?.last_name?.[0]]
    .filter(Boolean).join("").toUpperCase() || "U";

  // ── WebSocket ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!orgId) return;
    const base = (process.env.REACT_APP_BASE_URL || "http://localhost:8000/portal/")
      .replace(/\/portal\/?$/, "")
      .replace("https://", "wss://")
      .replace("http://", "ws://");

    let ws, reconnectTimer, destroyed = false;

    const connect = () => {
      // Don't attempt if already torn down (React StrictMode double-invoke guard)
      if (destroyed) return;

      ws = new WebSocket(`${base}/ws/org/${orgId}/`);
      wsRef.current = ws;

      ws.onmessage = (e) => {
        if (destroyed) return;
        const data = JSON.parse(e.data);
        if (data.type === "notification") {
          if (data.target === "pharmacy") return;
          const newNotif = {
            id:           String(data.notification_id),
            title:        data.title,
            message:      Array.isArray(data.message) ? data.message[0] : data.message,
            noti_type:    data.noti_type,
            entity_route: data.entity_route,
            entity_id:    data.entity_id ? String(data.entity_id) : null,
            created_at:   data.created_at,
            is_read:      false,
          };
          pendingWsIds.current.add(data.notification_id);
          setNotifications(prev => [newNotif, ...prev]);
          setUnreadCount(prev => prev + 1);
          toast.current?.show({
            severity: "info", summary: data.title, detail: data.message, life: 6000,
          });
        }
      };

      ws.onclose = () => {
        // Only reconnect if not intentionally destroyed and connection was actually opened
        if (!destroyed && ws.readyState !== WebSocket.CONNECTING) {
          reconnectTimer = setTimeout(connect, 3000);
        }
      };
    };

    connect();
    return () => {
      destroyed = true;
      clearTimeout(reconnectTimer);
      // Close only if socket exists and isn't already closed
      if (ws && ws.readyState !== WebSocket.CLOSED) ws.close();
    };
  }, [orgId]);

  // ── Fetch first page ──────────────────────────────────────────────────
  const fetchFirstPage = useCallback(() => {
    api.get(`portal-notifications/?page=1&page_size=${PAGE_SIZE}`)
      .then(res => {
        const fetched = res.data.notifications ?? [];
        setNotifications(prev => {
          const serverIds = new Set(fetched.map(n => n.id));
          const wsOnly = prev.filter(n => pendingWsIds.current.has(n.id) && !serverIds.has(n.id));
          fetched.forEach(n => pendingWsIds.current.delete(n.id));
          return [...wsOnly, ...fetched];
        });
        setUnreadCount(res.data.unread_count ?? 0);
        setPage(1);
        setHasMore(res.data.has_more ?? (res.data.notifications ?? []).length >= PAGE_SIZE);
      })
      .catch(() => {});
  }, []);

  // ── Load more (lazy scroll) ───────────────────────────────────────────
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    api.get(`portal-notifications/?page=${nextPage}&page_size=${PAGE_SIZE}`)
      .then(res => {
        const fetched = res.data.notifications ?? [];
        setNotifications(prev => {
          const existingIds = new Set(prev.map(n => n.id));
          return [...prev, ...fetched.filter(n => !existingIds.has(n.id))];
        });
        setPage(nextPage);
        setHasMore(res.data.has_more ?? fetched.length >= PAGE_SIZE);
      })
      .catch(() => {})
      .finally(() => setLoadingMore(false));
  }, [page, loadingMore, hasMore]);

  useEffect(() => { fetchFirstPage(); }, [fetchFirstPage]);

  const toggleBell = () => {
    if (!bellOpen) fetchFirstPage();
    setBellOpen(prev => !prev);
    setAvatarOpen(false);
  };

  const toggleAvatar = () => {
    setAvatarOpen(prev => !prev);
    setBellOpen(false);
  };

  const markAllRead = async () => {
    try {
      await api.post("portal-notifications/read/", {});
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch {}
  };

  const markOneRead = async (id) => {
    try {
      await api.post("portal-notifications/read/", { ids: [id] });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, is_read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch {}
  };

  const logout = () => {
    wsRef.current?.close();
    authLogout();
    navigate("/login");
  };

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <>
      <Toast ref={toast} position="top-right" style={{ zIndex: 9999 }} />

      <div className="portal-header">
        <button className="header-menu-btn" onClick={onMenuClick} aria-label="Open menu">
          <i className="pi pi-bars" style={{ fontSize: 16 }} />
        </button>

        <div className="portal-header-brand">
          <div className="portal-header-logo">
            <LogoSvg size={28} />
          </div>
          <div>
            <div className="portal-header-brand-name">FitMe</div>
            <div className="portal-header-brand-tagline">Care for your health</div>
          </div>
        </div>

        <div className="portal-header-right">
          <div className="portal-header-selector">
            <PharmacySelector />
          </div>

          {/* ── Bell ── */}
          <div style={{ position: "relative" }}>
            <div className="header-icon-btn" title="Notifications" onClick={toggleBell} style={{ cursor: "pointer" }}>
              <i className="pi pi-bell" style={{ fontSize: 15 }} />
              {/* ✅ Fix 1: prominent red badge instead of tiny dot */}
              {unreadCount > 0 && (
                <span style={{
                  position: "absolute",
                  top: -4, right: -4,
                  minWidth: 18, height: 18,
                  borderRadius: 9,
                  background: "#ef4444",
                  color: "white",
                  fontSize: 10, fontWeight: 700,
                  display: "flex", alignItems: "center", justifyContent: "center",
                  padding: "0 4px",
                  boxShadow: "0 0 0 2px var(--surface-card)",
                  lineHeight: 1,
                }}>
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </div>
            {bellOpen && (
              <NotificationDropdown
                notifications={notifications}
                unreadCount={unreadCount}
                hasMore={hasMore}
                loadingMore={loadingMore}
                onMarkRead={markAllRead}
                onMarkOneRead={markOneRead}
                onClose={() => setBellOpen(false)}
                onLoadMore={loadMore}
                navigate={navigate}
              />
            )}
          </div>

          {/* ── Avatar ── */}
          <div style={{ position: "relative" }}>
            <div className="header-avatar" title="Account" onClick={toggleAvatar} style={{ cursor: "pointer" }}>
              {initials}
            </div>
            {avatarOpen && (
              <AvatarDropdown
                user={user}
                onClose={() => setAvatarOpen(false)}
                navigate={navigate}
                onLogout={logout}
              />
            )}
          </div>
        </div>
      </div>
    </>
  );
}